import React, { useEffect, useReducer, useState } from "react";
import { Button, InputGroup, Form, Row } from "react-bootstrap";
import { Typography } from "@mui/material";
import { MARKETPLACE_CONSTANTS } from "../../../../../config/constants";
import { colors } from "../../../../../config/colors";
import {
  TEXT_ALIGN_CENTER,
  MAX_WIDTH,
  BORDER_SECONDARY,
} from "../../../../../config/styles";
import { Chips, SearchDropdown } from "../../../../../components";
import { API_URL } from "../../../../../utils/Constants";
import { ApiService } from "../../../../../utils/ApiService";
import { OVERRIDE_ACTION_TYPES } from "./types";

// Initial state
const initialState = {
  gradeSelected: "",
  divisionSelected: "",
  recipients: [],
  gradeDivisions: [],
  studentIds: [],
  overridePrice: "",
  hasError: {},
};

// Reducer function
const overridesReducer = (state, action) => {
  switch (action.type) {
    case OVERRIDE_ACTION_TYPES.SET_FIELD:
      return {
        ...state,
        [action.fieldName]: action.value,
        hasError: {
          ...state.hasError,
          [action.fieldName]: null,
        },
      };

    case OVERRIDE_ACTION_TYPES.SET_ERROR:
      return {
        ...state,
        hasError: {
          ...state.hasError,
          [action.fieldName]: action.error,
        },
      };

    case OVERRIDE_ACTION_TYPES.CLEAR_ERROR:
      return {
        ...state,
        hasError: {
          ...state.hasError,
          [action.fieldName]: null,
        },
      };

    case OVERRIDE_ACTION_TYPES.SET_RECIPIENTS:
      return {
        ...state,
        recipients: action.value,
      };

    case OVERRIDE_ACTION_TYPES.SET_GRADE_DIVISION_MAP:
      return {
        ...state,
        gradeDivisions: action.value,
      };

    case OVERRIDE_ACTION_TYPES.SET_STUDENT_IDS:
      return {
        ...state,
        studentIds: action.value,
      };

    case OVERRIDE_ACTION_TYPES.RESET_FORM:
      return {
        ...initialState,
      };

    default:
      return state;
  }
};

/**
 * OverrideForm Component
 *
 * Form for adding/editing product overrides (price overrides for specific grades/divisions/students)
 */
const OverrideForm = ({
  onDelete,
  initialValues,
  onSave,
  currency = "AED",
  gradesDivisionList = [],
  gradeList = [],
  merchantId,
}) => {
  const [state, dispatch] = useReducer(overridesReducer, initialState);
  const [divisionList, setDivisionList] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [studentList, setStudentList] = useState([]);

  // Handle save
  const handleSave = () => {
    const { recipients, gradeDivisions, studentIds, overridePrice } = state;
    if (recipients.length === 0) {
      return;
    }
    onSave({
      recipients: [...recipients],
      gradeDivisions: [...gradeDivisions],
      studentIds: [...studentIds],
      price: overridePrice,
    });
    resetForm();
  };

  // Load initial values when editing
  useEffect(() => {
    if (initialValues) {
      dispatch({
        type: OVERRIDE_ACTION_TYPES.SET_RECIPIENTS,
        value: initialValues.recipients || [],
      });
      dispatch({
        type: OVERRIDE_ACTION_TYPES.SET_GRADE_DIVISION_MAP,
        value: initialValues.gradeDivisions || [],
      });
      dispatch({
        type: OVERRIDE_ACTION_TYPES.SET_STUDENT_IDS,
        value: initialValues.studentIds || [],
      });
      dispatch({
        type: OVERRIDE_ACTION_TYPES.SET_FIELD,
        fieldName: "overridePrice",
        value: initialValues.price || "",
      });
    }
  }, [initialValues]);

  // Fetch student IDs for autocomplete
  const onFetchStudentId = async () => {
    try {
      if (studentId.length === 0) return;
      const url = `${API_URL.GET_STUDENT_IDS}/${merchantId}?studentId=${studentId}`;
      const resp = await ApiService.getRequest(url);
      if (resp.status && resp.status.code === "Y") {
        setStudentList(resp.body);
      }
    } catch (err) {
      console.error("Error fetching student IDs:", err);
    }
  };

  // Handle grade/division selection
  const handleSelectChange = (fieldName, e) => {
    const value = e?.target?.value;
    switch (fieldName) {
      case "grade": {
        const gradeIndex = parseInt(value) || 0;
        const selectedGradeItem = gradesDivisionList[gradeIndex];
        const gradeDivisions = selectedGradeItem?.divisions || [];
        dispatch({
          type: OVERRIDE_ACTION_TYPES.SET_FIELD,
          fieldName: "gradeSelected",
          value: value,
        });
        setDivisionList(gradeDivisions);
        dispatch({
          type: OVERRIDE_ACTION_TYPES.SET_FIELD,
          fieldName: "divisionSelected",
          value: "",
        });
        break;
      }
      case "division": {
        const divisionIndex = parseInt(value) || 0;
        const selectedDivisionItem = divisionList[divisionIndex];
        dispatch({
          type: OVERRIDE_ACTION_TYPES.SET_FIELD,
          fieldName: "divisionSelected",
          value: selectedDivisionItem || "",
        });
        break;
      }
      default:
        break;
    }
  };

  // Add grade/division to recipients
  const handleAddGradeDivision = () => {
    const gradeIndex = parseInt(state.gradeSelected);
    const gradeName = gradeList[gradeIndex];
    let recipient = gradeName;
    let mapObj = { grade: gradeName };

    if (state.divisionSelected) {
      recipient = `${recipient}, ${state.divisionSelected}`;
      mapObj = {
        grade: gradeName,
        division: state.divisionSelected,
      };
    }

    if (state.recipients.indexOf(recipient) !== -1) return;

    dispatch({
      type: OVERRIDE_ACTION_TYPES.SET_RECIPIENTS,
      value: [...state.recipients, recipient],
    });
    dispatch({
      type: OVERRIDE_ACTION_TYPES.SET_GRADE_DIVISION_MAP,
      value: [...state.gradeDivisions, mapObj],
    });
    dispatch({
      type: OVERRIDE_ACTION_TYPES.SET_FIELD,
      fieldName: "gradeSelected",
      value: "",
    });
    dispatch({
      type: OVERRIDE_ACTION_TYPES.SET_FIELD,
      fieldName: "divisionSelected",
      value: "",
    });
    setDivisionList([]);
  };

  // Remove recipient from list
  const onRemoveRecipient = (label, index) => {
    const newRecipients = [...state.recipients];
    newRecipients.splice(index, 1);

    // Check if this is a grade/division (contains comma) or student ID
    if (label.includes(",") || !label.match(/^\d+$/)) {
      // It's a grade/division - find and remove from gradeDivisions
      const gradeDivisionIndex = state.gradeDivisions.findIndex((item, idx) => {
        const displayLabel = item.division 
          ? `${item.grade}, ${item.division}` 
          : item.grade;
        return displayLabel === label;
      });
      if (gradeDivisionIndex !== -1) {
        const newGradeDivMapList = [...state.gradeDivisions];
        newGradeDivMapList.splice(gradeDivisionIndex, 1);
        dispatch({
          type: OVERRIDE_ACTION_TYPES.SET_GRADE_DIVISION_MAP,
          value: newGradeDivMapList,
        });
      }
    } else {
      // It's a student ID - find and remove from studentIds
      const studentIdIndex = state.studentIds.indexOf(label);
      if (studentIdIndex !== -1) {
        const newStudentIdList = [...state.studentIds];
        newStudentIdList.splice(studentIdIndex, 1);
        dispatch({
          type: OVERRIDE_ACTION_TYPES.SET_STUDENT_IDS,
          value: newStudentIdList,
        });
      }
    }

    dispatch({
      type: OVERRIDE_ACTION_TYPES.SET_RECIPIENTS,
      value: newRecipients,
    });
  };

  // Add student ID to recipients
  const onAddStudentId = (value) => {
    if (state.recipients.indexOf(value) !== -1) return;
    dispatch({
      type: OVERRIDE_ACTION_TYPES.SET_RECIPIENTS,
      value: [...state.recipients, value],
    });
    dispatch({
      type: OVERRIDE_ACTION_TYPES.SET_STUDENT_IDS,
      value: [...state.studentIds, value],
    });
  };

  // Reset form
  const resetForm = () => {
    dispatch({
      type: OVERRIDE_ACTION_TYPES.RESET_FORM,
    });
    setDivisionList([]);
    setStudentId("");
    setStudentList([]);
  };

  // Render recipient list
  const renderRecipientList = () => {
    return (
      <div className="receipent-list">
        {state.recipients.length === 0 ? (
          <Typography
            component="label"
            variant="subtitle2"
            sx={{
              textAlign: TEXT_ALIGN_CENTER,
              width: MAX_WIDTH,
              color: colors.grayLight,
            }}
          >
            Recipient List
          </Typography>
        ) : (
          <div className="d-flex flex-wrap">
            {state.recipients.map((recipt, indx) => (
              <div style={{ marginRight: 16, marginBottom: 16 }} key={recipt}>
                <Chips index={indx} label={recipt} onClose={onRemoveRecipient} />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Render search box for student ID
  const renderSearchBox = () => {
    return (
      <div className="my-3 w-100 mt-18">
        <SearchDropdown
          label="Student ID"
          value={studentId}
          width={MAX_WIDTH}
          disabled={state.recipients.length >= 20}
          items={studentList}
          onSelect={onAddStudentId}
          onChange={(event) => {
            setStudentId(event.target.value);
            onFetchStudentId();
          }}
        />
      </div>
    );
  };

  return (
    <div className="specification-box">
      {/* Grades */}
      <Form.Floating as={Row} className="mb-3 mx-1 w-100" controlId="">
        <Form.Control
          as="select"
          onChange={(e) => handleSelectChange("grade", e)}
          value={state.gradeSelected}
          required
          className="form-select"
        >
          <option value="" disabled></option>
          {gradeList.map((grade, index) => (
            <option key={`${grade}-${index}`} value={`${index}`}>
              {grade}
            </option>
          ))}
        </Form.Control>
        <label htmlFor="grade">
          <span>{MARKETPLACE_CONSTANTS.GRADE}</span>
          <span style={{ color: colors.warning }}> *</span>
        </label>
      </Form.Floating>

      {/* Division */}
      <Form.Floating as={Row} className="mb-3 mx-1 w-100" controlId="">
        <Form.Control
          as="select"
          onChange={(e) => handleSelectChange("division", e)}
          value={state.divisionSelected}
          className="form-select"
        >
          <option value="" disabled></option>
          {divisionList.map((division, index) => (
            <option key={`${division}-${index}`} value={index}>
              {division}
            </option>
          ))}
        </Form.Control>
        <label htmlFor="division">
          <span>{MARKETPLACE_CONSTANTS.DIVISION}</span>
        </label>
      </Form.Floating>

      {/* Add Grade/Division Button */}
      <div className="d-flex justify-content-end mb-3 mx-1 w-100">
        <Button
          className="generate-button"
          onClick={handleAddGradeDivision}
          disabled={!state.gradeSelected}
        >
          Add Grade/Division
        </Button>
      </div>

      {renderSearchBox()}
      {renderRecipientList()}

      {/* Price Input */}
      <InputGroup hasValidation className="w-100 mb-3 mt-3">
        <Form.Floating className="flex-grow-1">
          <Form.Control
            type="number"
            id="overridePrice"
            placeholder={MARKETPLACE_CONSTANTS.VARIANT_PRICE_LABEL}
            value={state.overridePrice}
            onChange={(e) =>
              dispatch({
                type: OVERRIDE_ACTION_TYPES.SET_FIELD,
                fieldName: "overridePrice",
                value: e.target.value,
              })
            }
            style={{ borderRight: "none" }}
          />
          <label htmlFor="overridePrice">
            <span>{MARKETPLACE_CONSTANTS.VARIANT_PRICE_LABEL}</span>
          </label>
        </Form.Floating>
        <Button
          style={{
            border: BORDER_SECONDARY,
            background: "transparent",
            borderLeft: "none",
          }}
        >
          <Typography variant="subtitle2">{currency}</Typography>
        </Button>
      </InputGroup>

      <div className="d-flex flex-row gap-3 w-100">
        <Button
          className="cancel-button w-100"
          onClick={() => {
            resetForm();
            onDelete();
          }}
        >
          Delete
        </Button>

        <Button
          className="generate-button w-100"
          onClick={handleSave}
          disabled={state.recipients.length === 0}
        >
          Done
        </Button>
      </div>
    </div>
  );
};

export default OverrideForm;
